import { PreviewSlide } from '../models/preview/preview.models';
import { customPeriodLabel } from './report-period';
import { richTextToPlain } from './report-rich-text';

/**
 * The reader's own words in a report, as blocks of their own: headings,
 * paragraphs, callouts and dividers.
 *
 * Not charts and not sections — nothing is read to draw them — and stored in a
 * list of their own (`text_blocks`). A block sits straight after the cover or
 * a section when its `after` names one, and after the sixteen fixed sections
 * and before the added charts when it names none, the way a chart and a picture
 * are placed (`report-placement.ts`). Among the blocks of one place, the order
 * is their order in the list.
 *
 * A paragraph and a callout are rich text (`report-rich-text.ts`), which is
 * also how a paragraph holds a list; a heading is one plain line; a divider has
 * no text at all.
 */

export type ReportTextBlockKind =
	| 'paragraph'
	| 'heading'
	| 'callout'
	| 'divider';

/** A text block as the report stores it — the backend's `ReportTextBlock`. */
export interface ReportTextBlock {
	/**
	 * Minted here, stable. It is what the history and Ana tell two blocks apart
	 * by: a position moves under them whenever a block above is added,
	 * dragged or removed.
	 */
	id: string;
	kind: ReportTextBlockKind;
	/** Rich text; one plain line on a heading; empty on a divider. */
	text: string;
	/** The reader typed `text` — absent when Ana wrote it. */
	edited_by_user?: boolean;
	// CONTRACT: the backend's ReportTextBlock.after, drawn by
	// report-placement.ts — absent is after the sections, before the charts.
	after?: string;
	/**
	 * What wrote it, when Ana wrote it from the report's own figures. Absent on
	 * every block a reader typed and on every block saved before it existed.
	 */
	written?: ReportTextWritten;
}

/** What Ana writes for a report from its own figures — the backend's `ReportWrittenTextKind`. */
export type ReportWrittenTextKind = 'summary' | 'next_steps' | 'briefing';

/**
 * What wrote a text block, when Ana did — the backend's `ReportTextWritten`:
 * the text asked for, and the window of the figures it was written from. The
 * window stays with the words after the report's own has moved on.
 */
export interface ReportTextWritten {
	kind: ReportWrittenTextKind;
	/** The report's period key when the text was written. */
	period: string;
	/** The window's first and last UTC days, `YYYY-MM-DD`. No first day is all of time. */
	start?: string;
	end: string;
}

/** A text Ana writes from the report's figures, as the section library offers it. */
export interface ReportWrittenTextOption {
	kind: 'summary' | 'next_steps';
	label: string;
	description: string;
	keywords: string;
}

// A competitor briefing is written by the same route, from its own chart, and
// is offered with that chart rather than here (T-2403).
export const REPORT_WRITTEN_TEXTS: ReportWrittenTextOption[] = [
	{
		kind: 'summary',
		label: 'Ana summary',
		description:
			"What this report's numbers say, written by Ana. Uses one AI summary",
		keywords: 'ana summary overview findings ai'
	},
	{
		kind: 'next_steps',
		label: 'Next steps',
		description:
			'Three to five actions, each quoting its figure. Uses one AI summary',
		keywords: 'next steps actions recommendations ana ai'
	}
];

/** What each written text is called in the editor. */
const WRITTEN_TEXT_NAMES: Record<ReportWrittenTextKind, string> = {
	summary: 'Ana summary',
	next_steps: 'Next steps',
	briefing: 'Competitor briefing'
};

/**
 * What Ana wrote, as the block it goes into the report as: a paragraph —
 * next steps are its numbered lines — whose words nobody typed.
 */
export function writtenTextBlock(
	text: string,
	written: ReportTextWritten
): ReportTextBlock {
	return { id: newTextBlockId(), kind: 'paragraph', text, written };
}

/**
 * The block with what Ana wrote again in place of its words: no longer the
 * reader's, and marked with the window it was written again for. Where it
 * sits and what it is drawn as stay.
 */
export function rewriteTextBlock(
	blocks: ReportTextBlock[],
	id: string,
	text: string,
	written: ReportTextWritten
): ReportTextBlock[] {
	return blocks.map(block => {
		if (block.id !== id) {
			return block;
		}
		const next: ReportTextBlock = {
			id: block.id,
			kind: block.kind,
			text,
			written
		};
		if (block.after) {
			next.after = block.after;
		}
		return next;
	});
}

/**
 * "Ana summary, written from the figures of 1 Sep – 30 Sep 2026" — what the
 * editor says under a block Ana wrote, so a reader can tell a text written for
 * last month from the charts beside it that read this one.
 */
export function writtenTextCaption(written: ReportTextWritten): string {
	const window = written.start
		? customPeriodLabel(written.start, written.end)
		: `everything up to ${customPeriodLabel(written.end, written.end)}`;
	return `${
		WRITTEN_TEXT_NAMES[written.kind]
	}, written from the figures of ${window}`;
}

/** Whether the editor can write the block again by itself: a briefing needs its chart. */
export function canWriteAgain(block: ReportTextBlock): boolean {
	return (
		block.written?.kind === 'summary' ||
		block.written?.kind === 'next_steps'
	);
}

/** The server's bound on the list (`SocialListeningReportSettings.text_blocks`). */
export const MAX_REPORT_TEXT_BLOCKS = 40;

/**
 * What a text block may hold. The server stores up to 4,000 characters of
 * markup; a paragraph is held to 2,000 the reader can see, which leaves its
 * links and marks room and still fits one page of the PDF.
 */
export const REPORT_TEXT_BLOCK_LIMITS = {
	heading: 120,
	body: 2000,
	stored: 4000
} as const;

/**
 * Everything the slash menu and the section library can put in a text block's
 * place, in the order they are offered. A list is a paragraph whose field
 * starts as one; a chart and an image are handed on to where charts and
 * images already come from — the library, and Ana.
 */
export type ReportTextInsertId =
	| 'paragraph'
	| 'heading'
	| 'bullets'
	| 'numbers'
	| 'callout'
	| 'divider'
	| 'chart'
	| 'image';

export interface ReportTextInsert {
	id: ReportTextInsertId;
	label: string;
	description: string;
	/** What typing after "/" matches, besides the label. */
	keywords: string;
}

export const REPORT_TEXT_INSERTS: ReportTextInsert[] = [
	{
		id: 'paragraph',
		label: 'Text',
		description: 'A paragraph of your own',
		keywords: 'text paragraph plain'
	},
	{
		id: 'heading',
		label: 'Heading',
		description: 'A title for what follows',
		keywords: 'heading title header h1 h2'
	},
	{
		id: 'bullets',
		label: 'Bulleted list',
		description: 'A list of points',
		keywords: 'bullet bulleted list points ul'
	},
	{
		id: 'numbers',
		label: 'Numbered list',
		description: 'Steps or a ranking, in order',
		keywords: 'numbered ordered list steps ol'
	},
	{
		id: 'callout',
		label: 'Callout',
		description: 'A takeaway set apart from the text',
		keywords: 'callout highlight note takeaway quote'
	},
	{
		id: 'divider',
		label: 'Divider',
		description: 'A line between parts of the report',
		keywords: 'divider line separator rule hr'
	},
	{
		id: 'chart',
		label: 'Chart',
		description: 'A chart from the section library',
		keywords: 'chart graph library section data'
	},
	{
		id: 'image',
		label: 'Image',
		description: 'A picture Ana draws for the report',
		keywords: 'image picture illustration photo'
	}
];

/** The kind of block an insert makes, when it makes one. */
export function insertKind(id: ReportTextInsertId): ReportTextBlockKind | null {
	switch (id) {
		case 'heading':
		case 'callout':
		case 'divider':
		case 'paragraph':
			return id;
		case 'bullets':
		case 'numbers':
			return 'paragraph';
		default:
			return null;
	}
}

/** A new block's id: unique among the report's, and within the server's 60. */
export function newTextBlockId(): string {
	return `txt_${Date.now().toString(36)}_${Math.random()
		.toString(36)
		.slice(2, 8)}`;
}

export function insertTextBlock(
	blocks: ReportTextBlock[],
	index: number,
	block: ReportTextBlock
): ReportTextBlock[] {
	const next = [...blocks];
	next.splice(Math.min(Math.max(index, 0), next.length), 0, block);
	return next;
}

/** A copy straight after the original, with an id of its own. */
export function duplicateTextBlock(
	blocks: ReportTextBlock[],
	id: string
): { blocks: ReportTextBlock[]; copy: ReportTextBlock | null } {
	const at = blocks.findIndex(block => block.id === id);
	if (at < 0) {
		return { blocks, copy: null };
	}
	const copy = { ...blocks[at], id: newTextBlockId() };
	return { blocks: insertTextBlock(blocks, at + 1, copy), copy };
}

export function removeTextBlock(
	blocks: ReportTextBlock[],
	id: string
): ReportTextBlock[] {
	return blocks.filter(block => block.id !== id);
}

/**
 * The same block as another kind. Its words go with it, except onto a
 * divider, which has none, and onto a heading, which is one plain line.
 */
export function retypeTextBlock(
	blocks: ReportTextBlock[],
	id: string,
	kind: ReportTextBlockKind
): ReportTextBlock[] {
	return blocks.map(block => {
		if (block.id !== id || block.kind === kind) {
			return block;
		}
		const text =
			kind === 'divider'
				? ''
				: kind === 'heading'
				? richTextToPlain(block.text)
						.replace(/\s+/g, ' ')
						.trim()
						.slice(0, REPORT_TEXT_BLOCK_LIMITS.heading)
				: block.text;
		const next: ReportTextBlock = { id: block.id, kind, text };
		if (block.edited_by_user && text) {
			next.edited_by_user = true;
		}
		if (block.after) {
			next.after = block.after;
		}
		// Only while its words are the ones Ana wrote: a heading keeps one
		// line of them, and a divider none.
		if (block.written && text === block.text) {
			next.written = block.written;
		}
		return next;
	});
}

/** Whether the block has anything to print — the PDF and the share leave out
 *  a block nobody typed into. */
export function isPrintableTextBlock(block: ReportTextBlock): boolean {
	return block.kind === 'divider' || !!richTextToPlain(block.text).trim();
}

// ── for Ana ──────────────────────────────────────────────────────────────────

/** A block the way Ana names it: "text 2", counting from one. */
export function textBlockName(index: number): string {
	return `text ${index + 1}`;
}

/** "text 2 (heading “Key findings”)", for a list Ana is shown. */
export function textBlockLabel(block: ReportTextBlock, index: number): string {
	const words = richTextToPlain(block.text).replace(/\s+/g, ' ').trim();
	const quoted =
		words.length > 40 ? `${words.slice(0, 39).trimEnd()}…` : words;
	const parts = [
		block.kind === 'paragraph' ? '' : block.kind,
		quoted ? `“${quoted}”` : block.kind === 'divider' ? '' : 'empty'
	].filter(Boolean);
	return `${textBlockName(index)} (${parts.join(' ')})`;
}

/**
 * The block a request names: "text 2" or "text block 2" by number, or failing
 * that the one block whose words contain what was asked for. -1 when it names
 * none, or more than one.
 */
export function findTextBlock(
	blocks: ReportTextBlock[],
	wanted: string
): number {
	const needle = wanted.trim().replace(/…$/, '').trim().toLowerCase();
	const numbered = /^text(?: block)?\s*#?(\d+)$/.exec(needle);
	if (numbered) {
		const index = Number(numbered[1]) - 1;
		return index >= 0 && index < blocks.length ? index : -1;
	}
	if (needle.length < 3) {
		return -1;
	}
	const matches = blocks
		.map((block, index) => ({
			index,
			words: richTextToPlain(block.text).toLowerCase()
		}))
		.filter(entry => entry.words.includes(needle));
	return matches.length === 1 ? matches[0].index : -1;
}

/**
 * A text block as the slide-edit agent reads a slide: one widget, the whole
 * block, marked when the reader typed it. A heading is a title to the agent,
 * which keeps it one plain line; a paragraph or a callout is summary text,
 * which it may format.
 */
export function textBlockEditSlide(block: ReportTextBlock): PreviewSlide {
	const shared = {
		id: block.id,
		visible: true,
		editedByUser: block.edited_by_user === true,
		content: { text: block.text }
	};
	return {
		id: `text_block_${block.id}`,
		slideType: 'text_block',
		title: '',
		widgets: [
			block.kind === 'heading'
				? { ...shared, kind: 'title' as const }
				: { ...shared, kind: 'summary_text' as const }
		]
	};
}

// ── for the PDF ──────────────────────────────────────────────────────────────

/**
 * Text blocks packed onto the export's fixed-height pages, as runs of indexes.
 *
 * The PDF is printed on a hard page box, so text cannot simply flow: a page
 * that is not exactly one page tall pushes every page after it out of
 * register. Blocks are measured as drawn and laid on a page until the next
 * would not fit. A block taller than a page — which the editor's limits keep
 * from happening — gets a page to itself.
 */
export function packTextPages(
	heights: number[],
	pageHeight: number,
	gap: number
): number[][] {
	const pages: number[][] = [];
	let page: number[] = [];
	let used = 0;
	heights.forEach((height, index) => {
		const needed = page.length ? used + gap + height : height;
		if (page.length && needed > pageHeight) {
			pages.push(page);
			page = [index];
			used = height;
			return;
		}
		page.push(index);
		used = needed;
	});
	if (page.length) {
		pages.push(page);
	}
	return pages;
}
